import { inject, type InjectionKey } from 'vue'
import type { CompletionOutcome } from './sign-in.ts'
import type { Auth } from './index.ts'

export const AUTH_KEY: InjectionKey<Auth> = Symbol('taler.auth')

export interface UseAuth {
  readonly signIn: Auth['signIn']
  completeSignIn(callbackUrl: string): Promise<CompletionOutcome>
  /** Absent outside a dev-server build - see `Auth['dev']`. */
  completeDevSignIn?(email: string): Promise<void>
  signOut(): Promise<void>
}

/** The only way a screen reaches authentication, and it never sees a token. */
export function useAuth(): UseAuth {
  const auth = inject(AUTH_KEY)
  if (auth === undefined) throw new Error('No auth provided. Install the app plugin first.')
  // `import.meta.env.DEV` folds to `false` in production, so `dev` is a literal `undefined`
  // there and this whole branch - `completeDevSignIn` included - drops out of the bundle.
  const dev = import.meta.env.DEV ? auth.dev : undefined
  return {
    signIn: auth.signIn,
    async completeSignIn(callbackUrl) {
      const outcome = await auth.signIn.complete(callbackUrl)
      if (outcome.kind === 'signed-in') auth.store.write(outcome.token)
      return outcome
    },
    ...(dev === undefined
      ? {}
      : { completeDevSignIn: async (email: string) => auth.store.write(await dev.signIn(email)) }),
    signOut: () => auth.signOut(),
  }
}
